/**
 * MUI system-props compatibility layer for the MD* components.
 *
 * The legacy Material-Dashboard components (MDBox, MDTypography, …) were built on MUI's
 * `Box`, which interprets a large set of "system props" (p, m, display, gap, …) and an
 * `sx` object as CSS. To remove the MUI dependency without rewriting the ~130 consumer
 * files, this helper reproduces the subset of that behavior the codebase actually uses:
 * it splits incoming props into a React `style` object plus the remaining DOM props.
 *
 * Spacing props use the MUI default 8px scale (`p={3}` → 24px). Sizing props (width,
 * height, top, …) follow MUI's rule: numbers ≤ 1 are fractions (→ %), other numbers are px.
 */
import type { CSSProperties } from 'react';

const SPACING_UNIT = 8;

// props whose numeric value is multiplied by the 8px spacing unit
const SPACING_PROPS: Record<string, (keyof CSSProperties)[]> = {
  p: ['padding'],
  pt: ['paddingTop'],
  pb: ['paddingBottom'],
  pl: ['paddingLeft'],
  pr: ['paddingRight'],
  px: ['paddingLeft', 'paddingRight'],
  py: ['paddingTop', 'paddingBottom'],
  m: ['margin'],
  mt: ['marginTop'],
  mb: ['marginBottom'],
  ml: ['marginLeft'],
  mr: ['marginRight'],
  mx: ['marginLeft', 'marginRight'],
  my: ['marginTop', 'marginBottom'],
  gap: ['gap'],
  rowGap: ['rowGap'],
  columnGap: ['columnGap'],
};

// props whose numeric value follows MUI's fraction/px sizing rule
const SIZING_PROPS = new Set([
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'top',
  'bottom',
  'left',
  'right',
]);

// props passed straight through to CSS (value used verbatim)
const PASSTHROUGH_PROPS = new Set([
  'display',
  'alignItems',
  'alignContent',
  'alignSelf',
  'justifyContent',
  'justifyItems',
  'justifySelf',
  'flexDirection',
  'flexWrap',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'order',
  'overflow',
  'overflowX',
  'overflowY',
  'position',
  'textAlign',
  'lineHeight',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'textTransform',
  'textOverflow',
  'whiteSpace',
  'wordBreak',
  'verticalAlign',
  'zIndex',
  'cursor',
  'borderRadius',
  'border',
  'borderTop',
  'borderBottom',
  'borderLeft',
  'borderRight',
  'borderColor',
  'boxShadow',
  'opacity',
  'visibility',
  'transition',
  'transform',
  'objectFit',
]);

function spacingValue(value: unknown): string | number | undefined {
  if (typeof value === 'number') return `${value * SPACING_UNIT}px`;
  if (typeof value === 'string') return value;
  return undefined;
}

function sizingValue(value: unknown): string | number | undefined {
  if (typeof value === 'number') {
    if (value <= 1 && value >= 0) return `${value * 100}%`;
    return `${value}px`;
  }
  if (typeof value === 'string') return value;
  return undefined;
}

/** Recursively flatten a flat `sx`-style object into CSS. Nested/selector keys are skipped. */
function sxToStyle(sx: Record<string, unknown>): CSSProperties {
  const style: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(sx)) {
    if (value == null) continue;
    // skip nested selectors / breakpoints / pseudo — not representable as flat inline style
    if (typeof value === 'object') continue;
    if (key in SPACING_PROPS) {
      for (const cssKey of SPACING_PROPS[key])
        style[cssKey] = spacingValue(value);
    } else if (SIZING_PROPS.has(key)) {
      style[key] = sizingValue(value);
    } else if (key === 'bgcolor' || key === 'backgroundColor') {
      style.backgroundColor = value;
    } else if (key === 'color') {
      style.color = value;
    } else {
      style[key] = value;
    }
  }
  return style as CSSProperties;
}

export interface SystemPropsResult<T extends object = Record<string, unknown>> {
  style: CSSProperties;
  rest: Partial<T>;
}

/**
 * Split system props out of a props bag into a `style` object plus the remaining
 * (DOM-bound) props. `style` and `sx` already present are merged in (explicit `style` wins).
 *
 * Generic over the input props so callers receive a `rest` typed for their own element
 * and stay free of `as` casts — the single boundary cast required by the runtime
 * key-split lives here in the utility, not in every component.
 */
export function extractSystemProps<T extends object>(
  props: T,
): SystemPropsResult<T> {
  const style: Record<string, unknown> = {};
  const rest: Record<string, unknown> = {};
  let explicitStyle: Record<string, unknown> | undefined;

  for (const [key, value] of Object.entries(props)) {
    if (value === undefined) continue;
    if (key === 'sx') {
      if (value && typeof value === 'object')
        Object.assign(style, sxToStyle(value as Record<string, unknown>));
      continue;
    }
    if (key === 'style') {
      if (value && typeof value === 'object')
        explicitStyle = value as Record<string, unknown>; // merged last so it wins
      continue;
    }
    if (key in SPACING_PROPS) {
      for (const cssKey of SPACING_PROPS[key])
        style[cssKey] = spacingValue(value);
    } else if (SIZING_PROPS.has(key)) {
      style[key] = sizingValue(value);
    } else if (PASSTHROUGH_PROPS.has(key)) {
      style[key] = value;
    } else {
      rest[key] = value;
    }
  }

  if (explicitStyle) Object.assign(style, explicitStyle);

  return { style: style as CSSProperties, rest: rest as Partial<T> };
}
